import React, { useState, useEffect, useCallback } from 'react';
import { format, parseISO, formatDistanceToNow } from 'date-fns';
import {
  Server, Database, Activity, Clock, HardDrive,
  AlertTriangle, CheckCircle,
} from 'lucide-react';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer, Legend,
} from 'recharts';
import observabilityService from '../../services/observabilityService';

const StatCard = ({ label, value, icon: Icon, color, loading }) => {
  const colorMap = {
    blue: { bg: 'bg-blue-100', text: 'text-blue-600' },
    green: { bg: 'bg-green-100', text: 'text-green-600' },
    yellow: { bg: 'bg-yellow-100', text: 'text-yellow-600' },
    red: { bg: 'bg-red-100', text: 'text-red-600' },
  };
  const colors = colorMap[color] || colorMap.blue;

  return (
    <div className="bg-white rounded-lg shadow-sm border p-6">
      <div className="flex items-center justify-between mb-4">
        <div className={`p-3 rounded-full ${colors.bg}`}>
          <Icon className={`h-6 w-6 ${colors.text}`} />
        </div>
      </div>
      {loading ? (
        <div className="space-y-2">
          <div className="h-8 bg-gray-200 rounded animate-pulse w-16" />
          <div className="h-4 bg-gray-200 rounded animate-pulse w-24" />
        </div>
      ) : (
        <>
          <p className="text-3xl font-bold text-gray-900">
            {value != null ? (typeof value === 'number' ? value.toLocaleString() : value) : '--'}
          </p>
          <p className="text-sm text-gray-500 mt-1">{label}</p>
        </>
      )}
    </div>
  );
};

const SectionError = ({ message }) => (
  <div className="bg-white rounded-lg shadow-sm border p-6 mb-6">
    <p className="text-sm text-red-600">{message}</p>
  </div>
);

const LoadingSkeleton = ({ rows = 5 }) => (
  <div className="space-y-3">
    {Array.from({ length: rows }).map((_, i) => (
      <div key={i} className="h-6 bg-gray-200 rounded animate-pulse" />
    ))}
  </div>
);

const getMethodBadgeClass = (method) => {
  switch (method) {
    case 'GET': return 'bg-blue-100 text-blue-700';
    case 'POST': return 'bg-green-100 text-green-700';
    case 'PUT': return 'bg-yellow-100 text-yellow-700';
    case 'DELETE': return 'bg-red-100 text-red-700';
    default: return 'bg-gray-100 text-gray-700';
  }
};

const getP95Color = (ms) => {
  if (ms < 200) return 'text-green-600';
  if (ms < 500) return 'text-yellow-600';
  return 'text-red-600';
};

const formatUptime = (uptimeStr) => {
  if (!uptimeStr) return '--';
  // Format: "5 days 06:32:15" or "06:32:15"
  const dayMatch = uptimeStr.match(/(\d+)\s*day/);
  const timeMatch = uptimeStr.match(/(\d+):(\d+):(\d+)/);
  const parts = [];
  if (dayMatch) parts.push(`${dayMatch[1]}d`);
  if (timeMatch) {
    if (parseInt(timeMatch[1]) > 0) parts.push(`${parseInt(timeMatch[1])}h`);
    parts.push(`${parseInt(timeMatch[2])}m`);
  }
  return parts.join(' ') || uptimeStr;
};

const SystemTab = () => {
  const [responseData, setResponseData] = useState(null);
  const [trendData, setTrendData] = useState(null);
  const [dbData, setDbData] = useState(null);
  const [containerData, setContainerData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sectionErrors, setSectionErrors] = useState({});

  const fetchData = useCallback(async () => {
    setLoading(true);
    setSectionErrors({});
    const results = await Promise.allSettled([
      observabilityService.getResponseTimes(),
      observabilityService.getResponseTimeTrend(),
      observabilityService.getDatabaseStats(),
      observabilityService.getContainerStatus(),
    ]);

    if (results[0].status === 'fulfilled') setResponseData(results[0].value);
    else setSectionErrors(prev => ({ ...prev, response: 'Failed to load response time data' }));

    if (results[1].status === 'fulfilled') setTrendData(results[1].value);
    else setSectionErrors(prev => ({ ...prev, trend: 'Failed to load response time trend data' }));

    if (results[2].status === 'fulfilled') setDbData(results[2].value);
    else setSectionErrors(prev => ({ ...prev, db: 'Failed to load database stats' }));

    if (results[3].status === 'fulfilled') setContainerData(results[3].value);
    else setSectionErrors(prev => ({ ...prev, container: 'Failed to load container status' }));

    setLoading(false);
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Compute overall avg and p95 from trend data
  const overallAvg = trendData?.trend?.length
    ? Math.round(trendData.trend.reduce((sum, d) => sum + d.avg_ms, 0) / trendData.trend.length)
    : null;
  const overallP95 = trendData?.trend?.length
    ? Math.round(trendData.trend.reduce((sum, d) => sum + d.p95_ms, 0) / trendData.trend.length)
    : null;

  // Prepare chart data
  const chartData = trendData?.trend?.map(d => ({
    ...d,
    label: (() => { try { return format(parseISO(d.hour), 'HH:mm'); } catch { return d.hour; } })(),
  })) || [];

  // Pool usage percentage
  const poolTotal = dbData?.pool ? (dbData.pool.pool_size || 0) + (dbData.pool.overflow || 0) : 0;
  const poolUsed = dbData?.pool?.checked_out || 0;
  const poolPercent = poolTotal > 0 ? Math.round((poolUsed / poolTotal) * 100) : 0;

  // Endpoint table (top 20)
  const endpoints = responseData?.endpoints?.slice(0, 20) || [];
  const hasMoreEndpoints = (responseData?.total_endpoints || 0) > 20;

  return (
    <div className="space-y-6">
      {/* Section A: API Response Times */}
      <div className="bg-white rounded-lg shadow-sm border p-6">
        <div className="flex items-center mb-4">
          <Activity className="h-5 w-5 text-gray-500 mr-2" />
          <h3 className="text-lg font-semibold text-gray-800">API Performance</h3>
        </div>
        {sectionErrors.response && <SectionError message={sectionErrors.response} />}
        {sectionErrors.trend && <SectionError message={sectionErrors.trend} />}
        {loading ? (
          <LoadingSkeleton rows={3} />
        ) : (
          <>
            {/* Stat cards */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
              <StatCard
                label="Endpoints Tracked"
                value={responseData?.total_endpoints}
                icon={Activity}
                color="blue"
                loading={false}
              />
              <StatCard
                label="Overall Avg Response"
                value={overallAvg != null ? `${overallAvg} ms` : null}
                icon={Clock}
                color="green"
                loading={false}
              />
              <StatCard
                label="Overall P95 Response"
                value={overallP95 != null ? `${overallP95} ms` : null}
                icon={Clock}
                color={overallP95 != null && overallP95 >= 500 ? 'red' : overallP95 != null && overallP95 >= 200 ? 'yellow' : 'green'}
                loading={false}
              />
            </div>

            {/* Trend chart */}
            {chartData.length > 0 && (
              <div className="mb-6">
                <h4 className="text-sm font-medium text-gray-700 mb-3">Response Time Trend (last 24h)</h4>
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart data={chartData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="label" tick={{ fontSize: 12 }} />
                    <YAxis label={{ value: 'ms', angle: -90, position: 'insideLeft' }} tick={{ fontSize: 12 }} />
                    <Tooltip
                      formatter={(value, name) => [`${value} ms`, name === 'avg_ms' ? 'Avg' : 'P95']}
                      labelFormatter={(label, payload) => {
                        if (payload?.[0]?.payload) {
                          const d = payload[0].payload;
                          return `${label} (${d.request_count?.toLocaleString() || 0} requests)`;
                        }
                        return label;
                      }}
                    />
                    <Legend formatter={(value) => value === 'avg_ms' ? 'Avg' : 'P95'} />
                    <Line type="monotone" dataKey="avg_ms" stroke="#3b82f6" strokeWidth={2} dot={false} />
                    <Line type="monotone" dataKey="p95_ms" stroke="#f59e0b" strokeWidth={2} dot={false} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            )}

            {/* Endpoints table */}
            {endpoints.length > 0 && (
              <div>
                <h4 className="text-sm font-medium text-gray-700 mb-3">Slowest Endpoints (by P95)</h4>
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Method</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Path</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Requests</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Avg (ms)</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">P95 (ms)</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Max (ms)</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {endpoints.map((ep, idx) => (
                        <tr key={idx} className="hover:bg-gray-50">
                          <td className="px-4 py-3 text-sm whitespace-nowrap">
                            <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${getMethodBadgeClass(ep.method)}`}>
                              {ep.method}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-900 font-mono whitespace-nowrap">{ep.path || ep.normalized_path}</td>
                          <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">{ep.request_count?.toLocaleString()}</td>
                          <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">{Math.round(ep.avg_ms)}</td>
                          <td className={`px-4 py-3 text-sm font-medium whitespace-nowrap ${getP95Color(ep.p95_ms)}`}>{Math.round(ep.p95_ms)}</td>
                          <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">{Math.round(ep.max_ms)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {hasMoreEndpoints && (
                  <p className="text-xs text-gray-400 mt-2">Showing top 20 of {responseData.total_endpoints} endpoints</p>
                )}
              </div>
            )}
          </>
        )}
      </div>

      {/* Section B: Database Statistics */}
      <div className="bg-white rounded-lg shadow-sm border p-6">
        <div className="flex items-center mb-4">
          <Database className="h-5 w-5 text-gray-500 mr-2" />
          <h3 className="text-lg font-semibold text-gray-800">Database</h3>
        </div>
        {sectionErrors.db && <SectionError message={sectionErrors.db} />}
        {loading ? (
          <LoadingSkeleton rows={3} />
        ) : (
          <>
            {/* Stat cards */}
            <div className="grid grid-cols-1 sm:grid-cols-4 gap-4 mb-6">
              <StatCard
                label="Database Size"
                value={dbData?.database?.database_size}
                icon={HardDrive}
                color="blue"
                loading={false}
              />
              <StatCard
                label="Active Connections"
                value={dbData?.connections?.active}
                icon={Activity}
                color="green"
                loading={false}
              />
              <StatCard
                label="Idle Connections"
                value={dbData?.connections?.idle}
                icon={Clock}
                color="yellow"
                loading={false}
              />
              <StatCard
                label="DB Uptime"
                value={formatUptime(dbData?.database?.uptime)}
                icon={Server}
                color="blue"
                loading={false}
              />
            </div>

            {/* Connection Pool */}
            {dbData?.pool && (
              <div className="mb-6">
                <h4 className="text-sm font-medium text-gray-700 mb-3">Connection Pool</h4>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-3">
                  <div className="text-center p-3 bg-gray-50 rounded-lg">
                    <p className="text-lg font-semibold text-gray-900">{dbData.pool.pool_size}</p>
                    <p className="text-xs text-gray-500">Pool Size</p>
                  </div>
                  <div className="text-center p-3 bg-gray-50 rounded-lg">
                    <p className="text-lg font-semibold text-green-600">{dbData.pool.checked_in}</p>
                    <p className="text-xs text-gray-500">Checked In</p>
                  </div>
                  <div className="text-center p-3 bg-gray-50 rounded-lg">
                    <p className="text-lg font-semibold text-yellow-600">{dbData.pool.checked_out}</p>
                    <p className="text-xs text-gray-500">Checked Out</p>
                  </div>
                  <div className="text-center p-3 bg-gray-50 rounded-lg">
                    <p className="text-lg font-semibold text-gray-600">{dbData.pool.overflow}</p>
                    <p className="text-xs text-gray-500">Overflow</p>
                  </div>
                </div>
                {/* Pool usage bar */}
                <div className="w-full bg-gray-200 rounded-full h-3">
                  <div
                    className={`h-3 rounded-full ${poolPercent > 80 ? 'bg-red-500' : poolPercent > 50 ? 'bg-yellow-500' : 'bg-green-500'}`}
                    style={{ width: `${Math.min(poolPercent, 100)}%` }}
                  />
                </div>
                <p className="text-xs text-gray-500 mt-1">{poolPercent}% pool utilization ({poolUsed}/{poolTotal})</p>
              </div>
            )}

            {/* Table Sizes */}
            {dbData?.tables?.length > 0 && (
              <div className="mb-6">
                <h4 className="text-sm font-medium text-gray-700 mb-3">Table Sizes (Top 20)</h4>
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Table Name</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Size</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Rows</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Dead Rows</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Last Vacuum</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {dbData.tables.map((tbl, idx) => {
                        const deadRatio = tbl.estimated_rows > 0 ? tbl.dead_rows / tbl.estimated_rows : 0;
                        const highDeadRows = deadRatio > 0.1;
                        return (
                          <tr key={idx} className={`hover:bg-gray-50 ${highDeadRows ? 'bg-yellow-50' : ''}`}>
                            <td className="px-4 py-3 text-sm text-gray-900 font-mono whitespace-nowrap">{tbl.table_name}</td>
                            <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">{tbl.total_size}</td>
                            <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">{tbl.estimated_rows?.toLocaleString()}</td>
                            <td className={`px-4 py-3 text-sm whitespace-nowrap ${highDeadRows ? 'text-yellow-700 font-medium' : 'text-gray-700'}`}>
                              {tbl.dead_rows?.toLocaleString()}
                              {highDeadRows && (
                                <span className="ml-1 text-xs text-yellow-600">({Math.round(deadRatio * 100)}%)</span>
                              )}
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-500 whitespace-nowrap">
                              {tbl.last_autovacuum
                                ? (() => { try { return formatDistanceToNow(parseISO(tbl.last_autovacuum), { addSuffix: true }); } catch { return tbl.last_autovacuum; } })()
                                : 'Never'}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Slow Queries */}
            <div>
              <h4 className="text-sm font-medium text-gray-700 mb-3">Slow Queries</h4>
              {dbData?.slow_queries?.length > 0 ? (
                <>
                  <div className="flex items-center gap-2 px-3 py-2 bg-yellow-50 rounded-lg mb-3">
                    <AlertTriangle className="h-4 w-4 text-yellow-600" />
                    <span className="text-sm text-yellow-700 font-medium">
                      {dbData.slow_queries.length} slow {dbData.slow_queries.length === 1 ? 'query' : 'queries'} currently running
                    </span>
                  </div>
                  <div className="space-y-2">
                    {dbData.slow_queries.map((sq, idx) => (
                      <div key={idx} className="p-3 bg-gray-50 rounded-lg border border-gray-200">
                        <div className="flex items-center gap-4 mb-1">
                          <span className="text-xs text-gray-500">PID: {sq.pid}</span>
                          <span className="text-xs text-gray-500">User: {sq.usename}</span>
                          <span className="text-xs font-medium text-yellow-700">Duration: {Math.round(sq.duration_seconds)}s</span>
                        </div>
                        <p className="text-sm text-gray-800 font-mono truncate" title={sq.query_preview}>
                          {sq.query_preview}
                        </p>
                      </div>
                    ))}
                  </div>
                </>
              ) : (
                <div className="flex items-center gap-2 px-3 py-2 bg-green-50 rounded-lg">
                  <CheckCircle className="h-4 w-4 text-green-600" />
                  <span className="text-sm text-green-700 font-medium">No slow queries</span>
                </div>
              )}
            </div>
          </>
        )}
      </div>

      {/* Section C: Infrastructure / Container Status */}
      <div className="bg-white rounded-lg shadow-sm border p-6">
        <div className="flex items-center mb-4">
          <Server className="h-5 w-5 text-gray-500 mr-2" />
          <h3 className="text-lg font-semibold text-gray-800">Infrastructure</h3>
        </div>
        {sectionErrors.container && <SectionError message={sectionErrors.container} />}
        {loading ? (
          <LoadingSkeleton rows={3} />
        ) : containerData?.docker_available ? (
          <>
            {/* Docker containers grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {containerData.containers?.map((c, idx) => {
                const isUp = c.status?.toLowerCase()?.includes('up');
                return (
                  <div key={idx} className={`p-4 rounded-lg border-2 ${isUp ? 'border-green-300' : 'border-red-300'}`}>
                    <div className="flex items-center gap-2 mb-2">
                      <div className={`w-2.5 h-2.5 rounded-full ${isUp ? 'bg-green-500' : 'bg-red-500'}`} />
                      <h5 className="text-sm font-semibold text-gray-900">{c.name}</h5>
                    </div>
                    <p className="text-xs text-gray-500 truncate mb-2" title={c.image}>{c.image}</p>
                    <div className="grid grid-cols-2 gap-2 text-xs">
                      <div>
                        <span className="text-gray-500">Status:</span>
                        <span className={`ml-1 font-medium ${isUp ? 'text-green-700' : 'text-red-700'}`}>{c.status}</span>
                      </div>
                      {(c.cpu != null || c.cpu_percent != null) && (
                        <div>
                          <span className="text-gray-500">CPU:</span>
                          <span className="ml-1 font-medium text-gray-900">{c.cpu || c.cpu_percent}%</span>
                        </div>
                      )}
                      {(c.mem_usage || c.memory_usage) && (
                        <div>
                          <span className="text-gray-500">Memory:</span>
                          <span className="ml-1 font-medium text-gray-900">{c.mem_usage || c.memory_usage}</span>
                        </div>
                      )}
                      {(c.net_io || c.network_io) && (
                        <div>
                          <span className="text-gray-500">Net I/O:</span>
                          <span className="ml-1 font-medium text-gray-900">{c.net_io || c.network_io}</span>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        ) : (
          <>
            {/* Docker unavailable fallback */}
            <div className="flex items-center gap-2 px-3 py-2 bg-blue-50 rounded-lg mb-4">
              <Server className="h-4 w-4 text-blue-600" />
              <span className="text-sm text-blue-700 font-medium">Docker monitoring unavailable -- showing available system metrics</span>
            </div>

            {containerData?.system_info && (
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-4">
                <div className="text-center p-4 bg-gray-50 rounded-lg">
                  <p className="text-lg font-semibold text-gray-900">
                    {containerData.system_info.database_uptime
                      ? formatUptime(containerData.system_info.database_uptime)
                      : '--'}
                  </p>
                  <p className="text-xs text-gray-500">Database Uptime</p>
                </div>
                <div className="text-center p-4 bg-gray-50 rounded-lg">
                  <p className="text-lg font-semibold text-gray-900">
                    {containerData.system_info.active_connections ?? '--'}
                  </p>
                  <p className="text-xs text-gray-500">Active Connections</p>
                </div>
                <div className="text-center p-4 bg-gray-50 rounded-lg">
                  <p className="text-lg font-semibold text-gray-900">
                    {containerData.system_info.pool_status
                      ? `${containerData.system_info.pool_status.checked_out}/${containerData.system_info.pool_status.pool_size}`
                      : '--'}
                  </p>
                  <p className="text-xs text-gray-500">Pool (Used/Size)</p>
                </div>
              </div>
            )}

            <p className="text-xs text-gray-400">
              Mount Docker socket (read-only) to enable full container monitoring.
            </p>
          </>
        )}
      </div>
    </div>
  );
};

export default SystemTab;
